import { Button, ConnectionIndicator, DocumentIcon, Toast } from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { Label } from './Label';
import {
  selectCaseIdData,
  selectServerLastCommunication,
  selectSharedDataActiveTimelineChronologyType,
  selectUserAboutData,
  TimelineChronologyType,
  useAppSelector,
} from '@/store';
import { isEndpointLockError, useLazyGetCaseQuery, useLazyPostCaseEditQuery, usePostCaseInfoEditMutation } from '@/api';
import { format } from 'date-fns';
import { NumberPagesBySwitcher } from './NumberPagesBySwitcher';
import { TimelineChronologyUndoRedoButtons } from './TimelineChronologyUndoRedoButtons';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { DetailsDialog, DetailsDialogFormValues, DetailsDialogInitialData } from './DetailsDialog';
import { ChronologyDownloadButton } from './ChronologyDownloadButton';

/** Serializes a Date to the ISO date string the backend expects (yyyy-MM-dd). */
const serializeDob = (date: Date | null): string | null => (date ? format(date, 'yyyy-MM-dd') : null);

interface ActionsPanelProps {
  newEntryIndex: number | null;
  editEntryIndex: number | null;
  handleAddNewEntry: (index: number) => void;
  toastRef: RefObject<Toast | null>;
}

const hasAnyMissingField = (data: DetailsDialogInitialData | null | undefined): boolean => {
  if (!data) return true;
  const fields = [
    data.name,
    data.dateOfBirth,
    data.additionalReference,
    data.preExistingConditions,
    data.surgicalProcedures,
  ];
  return fields.some((v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === ''));
};

export const ActionsPanel = ({ newEntryIndex, editEntryIndex, handleAddNewEntry, toastRef }: ActionsPanelProps) => {
  const serverLastCommunication = useAppSelector(selectServerLastCommunication);
  const caseId = useAppSelector(selectCaseIdData);
  const activeType = useAppSelector(selectSharedDataActiveTimelineChronologyType);
  const userAboutData = useAppSelector(selectUserAboutData);

  // Read: the BE returns the chronology client-details under `additionalInfo`
  // (sibling to `info`) on the `case?case_id=…` response — single GET.
  //
  // Write: the four `additionalInfo` fields (clientName / clientDob /
  // pastMedicalHistory / surgicalProcedures) go to `case/case-info/edit` flat
  // in the body — that endpoint silently drops `gRef`. The Additional reference
  // field (which maps to `gRef`) goes to `case/edit` instead. We fire both
  // POSTs in parallel on save.
  const [triggerGetCase, { data: caseData }] = useLazyGetCaseQuery();
  const [triggerPostCaseInfoEdit, { isLoading: isSavingCaseInfo }] = usePostCaseInfoEditMutation();
  const [triggerPostCaseEdit, { isFetching: isSavingCaseEdit }] = useLazyPostCaseEditQuery();
  const isSavingDetails = isSavingCaseInfo || isSavingCaseEdit;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const isChronology = activeType === TimelineChronologyType.CHRONOLOGY;
  const canShowDetailsButton = Boolean(userAboutData?.isMls) && isChronology;

  // Combine the two BE places the modal cares about: additionalReference is
  // really `info.gRef`; the other four come from `additionalInfo` (which uses
  // its own naming: clientName / clientDob / pastMedicalHistory / surgicalProcedures).
  const detailsInitialData: DetailsDialogInitialData | null = useMemo(() => {
    if (!caseData) return null;
    const ai = caseData.additionalInfo;
    return {
      name: ai?.clientName ?? null,
      dateOfBirth: ai?.clientDob ?? null,
      additionalReference: caseData.info?.gRef ?? null,
      preExistingConditions: ai?.pastMedicalHistory ?? null,
      surgicalProcedures: ai?.surgicalProcedures ?? null,
    };
  }, [caseData]);

  // Fetch the case on chronology page load (and whenever the case changes) so
  // the red-dot is accurate before the user opens the modal. caseEdit's
  // CASE_METADATA_TAG invalidation will trigger a refetch after save.
  useEffect(() => {
    if (canShowDetailsButton && caseId) {
      triggerGetCase({ caseId });
    }
  }, [canShowDetailsButton, caseId, triggerGetCase]);

  const hasMissingDetails = useMemo(() => hasAnyMissingField(detailsInitialData), [detailsInitialData]);

  const handleOpenDetails = useCallback(() => {
    if (caseId) {
      // Refresh first so the modal opens with the latest server state.
      triggerGetCase({ caseId });
    }
    setIsDetailsOpen(true);
  }, [caseId, triggerGetCase]);

  const handleSaveDetails = useCallback(
    async (values: DetailsDialogFormValues): Promise<boolean | null> => {
      if (!caseId) return false;
      try {
        // The modal's five form fields are split across two BE endpoints:
        //  - The four client-detail fields (clientName / clientDob /
        //    pastMedicalHistory / surgicalProcedures) → POST case/case-info/edit
        //    flat in the body (no `additionalInfo` wrapper). Sending `gRef`
        //    here makes that endpoint silently discard the request.
        //  - additionalReference → POST case/edit as `gRef`.
        // Both POSTs fire in parallel via Promise.all; if either rejects we
        // return false so the modal can surface an error toast, or null when
        // an endpoint lock refused it and has shown its own.
        const trimmedRef = values.additionalReference.trim();
        await Promise.all([
          triggerPostCaseInfoEdit({
            caseId,
            clientName: values.name.trim(),
            clientDob: serializeDob(values.dateOfBirth),
            pastMedicalHistory: values.preExistingConditions.trim(),
            surgicalProcedures: values.surgicalProcedures.trim(),
          }).unwrap(),
          triggerPostCaseEdit({
            caseId,
            gRef: trimmedRef === '' ? null : trimmedRef,
          }).unwrap(),
        ]);

        // Refresh so the red-dot indicator updates immediately. Both
        // mutations also invalidate CASE_METADATA_TAG, so any other
        // subscribers will refetch automatically too.
        triggerGetCase({ caseId });
        return true;
      } catch (error) {
        console.error('Failed to save case details:', error);
        // The lock's own toast has already explained a refusal, so the modal stays open without another
        return isEndpointLockError(error) ? null : false;
      }
    },
    [caseId, triggerPostCaseInfoEdit, triggerPostCaseEdit, triggerGetCase],
  );

  return (
    <div className="flex w-full flex-col">
      <div className="bg-fletchers-white border-fletchers-gray-110 flex h-full w-full flex-row items-center justify-center gap-2 border-b px-6 py-[8px]">
        <Label />
        <div className="mx-[8px]">
          <ConnectionIndicator lastCommunication={serverLastCommunication} />
        </div>
        <TimelineChronologyUndoRedoButtons />
        {canShowDetailsButton && (
          <div className="relative">
            <Button
              outlined
              icon={<DocumentIcon className="h-[13px] w-[13px] text-black" />}
              aria-label="Details"
              onClick={handleOpenDetails}
              className="!h-[32px] !w-[32px] !px-[9px] !py-[9px] hover:!bg-gray-50"
            />
            {hasMissingDetails ? (
              <span
                aria-label="Missing details"
                className="bg-fletchers-red-500 absolute -top-[4px] -right-[4px] h-[8px] w-[8px] rounded-full"
              />
            ) : (
              <span
                aria-label="All details complete"
                className="bg-fletchers-green-300 absolute -top-[4px] -right-[4px] h-[8px] w-[8px] rounded-full"
              />
            )}
          </div>
        )}
        <ChronologyDownloadButton toastRef={toastRef} />
        <OutlineButton
          label="Create entry"
          icon="pi pi-plus !text-[12px]"
          className="!text-[14px] whitespace-nowrap"
          onClick={() => handleAddNewEntry(0)}
        />

        <div className="ml-auto flex flex-row items-center gap-2 whitespace-nowrap">
          <span className="text-[14px]/[20px] font-[400]">Number pages by</span>
          <NumberPagesBySwitcher disabled={Boolean(newEntryIndex || editEntryIndex)} />
        </div>
      </div>

      {isDetailsOpen && (
        <DetailsDialog
          initialData={detailsInitialData}
          isSaving={isSavingDetails}
          onClose={() => setIsDetailsOpen(false)}
          onSave={handleSaveDetails}
          toastRef={toastRef}
        />
      )}
    </div>
  );
};
